import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { SYSTEMS, SYSTEM_BY_KEY } from "@/lib/sensory";
import { TID } from "@/lib/testIds";

// mode = "student" | "explore"
export default function BoxesGrid({ mode = "student" }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const nav = useNavigate();

  const homePath = mode === "student" ? "/play" : "/explore";
  const detailBase = mode === "student" ? "/play/boxes" : "/explore/boxes";
  const endpoint = mode === "student" ? "/me/boxes" : "/public/boxes";

  React.useEffect(() => {
    api.get(endpoint).then(({ data }) => setData(data)).finally(() => setLoading(false));
  }, [endpoint]);

  if (loading) return <div className="text-[var(--sw-ink-soft)]">Opening the boxes…</div>;

  const systems = (data?.systems || []).map((s) => ({ ...s, ...(SYSTEM_BY_KEY[s.system] || {}) }));
  const allOff = systems.length === 0;

  return (
    <div data-testid={TID.studentBoxes} className="space-y-8">
      <div className="flex items-center gap-3">
        <Link to={homePath} className="sw-pill h-11"><ArrowLeft size={16} /> Back</Link>
      </div>
      <header>
        <div className="text-xs uppercase tracking-widest text-[var(--sw-ink-soft)]">Sensory boxes</div>
        <h1 className="display mt-1 text-4xl md:text-6xl font-extrabold leading-tight">Pick a box.</h1>
        <p className="mt-2 text-lg text-[var(--sw-ink-soft)]">Tap to open. Bubbles will float up. Pop one to try it.</p>
      </header>

      {allOff ? (
        <div className="sw-card p-10 text-center text-[var(--sw-ink-soft)]">
          No boxes are open for you yet. Ask your therapist to turn some on.
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {systems.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div key={s.key} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 * i }}>
                <Link
                  to={`${detailBase}/${s.key}`}
                  data-testid={TID.studentBoxTile(s.key)}
                  className="block rounded-3xl border-2 p-6 touch-target relative overflow-hidden transition hover:shadow-lg"
                  style={{ background: s.bg, borderColor: s.border, minHeight: 180 }}
                >
                  <div className="rounded-2xl inline-flex p-2.5" style={{ background: "rgba(255,255,255,0.6)", color: s.color }}>
                    <Icon size={28} strokeWidth={2} />
                  </div>
                  <div className="display text-xl md:text-2xl font-bold mt-4 leading-tight" style={{ color: s.color }}>
                    {s.studentLabel}
                  </div>
                  <div className="text-xs mt-1 font-semibold uppercase tracking-wider" style={{ color: s.color, opacity: 0.8 }}>
                    {s.count} bubble{s.count === 1 ? "" : "s"}
                  </div>
                  <div aria-hidden className="absolute -bottom-4 -right-4 w-24 h-24 rounded-full opacity-25"
                    style={{ background: `radial-gradient(circle at 35% 35%, #fff, ${s.color})` }}
                  />
                </Link>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
